import isString from "lodash/isString";
import {
  phoneNumberPrefixes,
  TRIP_STATES,
  TRIP_STATUS,
  TRIP_STATUS_OPTIONS,
  STATUS_COLORS,
  STATUS_BOOL_COLORS,
  STATUS
} from "../constants/index";

export const formatToPhoneNumber = (str) => {
  if (!str) {
    return { phoneNumber: "", prefix: "", fullPhoneNumber: "" };
  }
  const predefinedPrefix = phoneNumberPrefixes.find((pPrefix) =>
    str.startsWith(pPrefix.value)
  );

  const prefixLength = predefinedPrefix ? predefinedPrefix.value.length : 0;

  const prefix = str.substring(0, prefixLength);
  const phoneNumber = str.substring(prefixLength);
  const phoneNumberFormatted = phoneNumber
    ? phoneNumber.replace(/\D*(\d{3})\D*(\d{3})\D*(\d{4})\D*/, "$1 $2 $3")
    : "";

  return {
    fullPhoneNumber: `${prefix}${phoneNumberFormatted}`,
    phoneNumber: phoneNumberFormatted,
    prefix
  };
};

export const compose =
  (...fns) =>
    (arg) =>
      fns.reduce((prev, fn) => fn(prev), arg);

export const addHours = (hours, date = new Date()) => {
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  return date;
};

export function debounce(func, wait) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      func.apply(context, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function isTripDeleted(status) {
  return (
    status === STATUS.deletionAccepted ||
    status === STATUS.deletionAccepted
  );
}

export function isStatusAnError(status) {
  return (
    status === STATUS.syntaxError ||
    status === STATUS.syntaxRejected
  );
}

export function isStatusRejected(status) {
  return (
    status === STATUS.rejected ||
    status === STATUS.rejectedClosingTrip ||
    status === STATUS.rejectedReturnToPreliminary ||
    status === STATUS.rejectedTripPortion ||
    status === STATUS.rejectedUpdateTripPortion
  );
}

export function getShipmentStatusColor(status) {
  switch (status) {
    case TRIP_STATUS_OPTIONS.entryNotOnFile.value:
      return {
        color: "bg-yellow-400",
        label: TRIP_STATUS_OPTIONS.entryNotOnFile.label
      };
    case TRIP_STATUS_OPTIONS.entryOnFile.value:
      return {
        color: "bg-green-500",
        label: TRIP_STATUS_OPTIONS.entryOnFile.label
      };
    default:
      return {
        color: "bg-gray-400",
        label: null
      };
  }
}

export function getShipmentStatusGeneralColor(status) {
  switch (status) {
    case 'sent':
    case 'sentForDeletion':
    case 'sentForEdition':
      return {
        color: "bg-blue-400",
        label: "Sent"
      };

    case 'accepted':
    case 'editionAccepted':
    case 'entryOnFile':
    case 'entryNotOnFile':
    case 'qpOnFile':
      return {
        color: "bg-green-500",
        label: "Accepted"
      };

    case 'rejected':
    case 'editionRejected':
    case 'deletionRejected':
      return {
        color: "bg-red-500",
        label: "Rejected"
      };

    case 'deleted':
    case 'deletionAccepted':
      return {
        color: "bg-red-400",
        label: "Deleted"
      };

    case 'inactive':
      return {
        color: "bg-gray-400",
        label: "Inactive"
      };

    case 'received':
      return {
        color: "bg-yellow-400",
        label: "Received"
      };

    case 'entryPort':
      return {
        color: "bg-purple-400",
        label: "Entry Port"
      };

    case 'overage':
      return {
        color: "bg-orange-400",
        label: "Overage"
      };

    case 'shortage':
      return {
        color: "bg-red-400",
        label: "Shortage"
      };

    default:
      return {
        color: "bg-gray-400",
        label: status
      };
  }
}

export function getStatusColor({ status, state }) {
  if (isStatusRejected(status)) {
    return {
      status,
      color: "bg-pink-700",
      textColor: "text-pink-700",
      labelColor: "text-white",
      label: TRIP_STATUS.ERROR.LABEL[status]
    };
  } else if (status === TRIP_STATES.ENTRY_NOT_ON_FILE.VALUE) {
    return {
      status,
      labelColor: "text-white",
      textColor: "text-primary-400",
      color: "bg-primary-400",
      label: "Entry Not On File"
    };
  } else if (status === TRIP_STATES.ENTRY_ON_FILE.VALUE) {
    return {
      status,
      labelColor: "text-white",
      textColor: "text-black",
      color: "bg-black",
      label: "Accepted"
    };
  }

  const statusKey = Object.keys(TRIP_STATUS).find((key) => {
    const statusObject = TRIP_STATUS[key];
    if (statusObject.VALUE instanceof Array) {
      return (
        statusObject.VALUE.includes(status)
      );
    }
    return statusObject.VALUE === status;
  });

  if (!statusKey) {
    return TRIP_STATUS.DRAFT;
  }

  const { LABEL_COLOR, LABEL, COLOR, TEXT_COLOR } = TRIP_STATUS[statusKey];

  if (statusKey === "ERROR" || statusKey === "IN_PROGRESS") {
    return {
      status: statusKey,
      labelColor: LABEL_COLOR,
      color: COLOR,
      label: TRIP_STATUS[statusKey].LABEL[status]
    };
  }

  return {
    status: statusKey,
    labelColor: LABEL_COLOR,
    textColor: TEXT_COLOR,
    color: COLOR,
    label: LABEL
  };
}

export function getTripStatusColor(status) {

  let result = STATUS_COLORS[status];

  if (result) {
    return {
      status: status,
      label: result?.label,
      color: result?.color
    };
  }
  else {
    return {
      status: status,
      label: status,
      color: 'bg-red-400'
    };
  }
}

export function getBoolStatusColor(status) {

  let result = STATUS_BOOL_COLORS[status];

  return {
    status: status,
    label: result.label,
    color: result.color
  };
}

export function areAllFounded(arr1, arr2) {
  return arr2.every((a) => arr1.includes(a));
}

export function mapCatalogKeys(catalogsCollection) {
  return catalogsCollection.map((catalog) => {
    if (isString(catalog)) {
      return catalog;
    } else if (catalog.hasOwnProperty("propName")) {
      return catalog.propName;
    }
    return catalog.name;
  });
}

export function mapValues(source, target) {
  return Object.keys(target).reduce(
    (prev, key) => ({ ...prev, [key]: source[key] }),
    {}
  );
}

export function arrToObject(arr, defaultValue = null) {
  return arr.reduce((prev, key) => ({ ...prev, [key]: defaultValue }), {});
}

export const sortByIdsList = (idsList, idPropName, arr) => {
  if (!idsList || !arr) return [];
  const order = {};
  idsList.forEach(function (id, i) {
    order[id] = i + 1;
  });
  return arr.sort(function (a, b) {
    return order[a[idPropName]] - order[b[idPropName]];
  });
};

export const mapMatchingKeys = (source, target) => {
  return Object.keys(source).reduce((prev, key) => {
    prev[key] = target[key] || source[key];
    return prev;
  }, {});
};

export const toCamelCase = function (string) {
  return string.replace(/\s+(.)/g, function (_, group) {
    return group.toUpperCase();
  });
};

export const getKeyByValue = (object, value) => {
  return Object.keys(object).find((key) => object[key] === value);
};

export const isNumeric = (str) => {
  if (typeof str != "string") return false;
  return !isNaN(str) && !isNaN(parseFloat(str));
};

export const ToCurrency = (number, decimals = 2) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    maximumSignificantDigits: decimals
  }).format(number);
};

export const generateId = () => {
  return "" + Math.floor(3000 + Math.random() * 2000 + Math.random() * 1000);
};

export const allIncluded = (arr, target) => {
  return target.every((v) => arr.includes(v));
};

export const mergeCatalogs = (catalogItems, source) => {
  return Object.keys(catalogItems).reduce(
    (prev, catalogKey) => {
      const [dataProp] = Object.keys(catalogItems[catalogKey]);

      prev[catalogKey] = {
        ...prev[catalogKey],
        [dataProp]: [
          ...prev[catalogKey][dataProp],
          ...catalogItems[catalogKey][dataProp]
        ]
      };
      return prev;
    },
    { ...source }
  );
};

export const mapObj = (source, target) => {
  if (!target) return source;
  return Object.entries(source).reduce(
    (prev, [key, value]) => ({
      ...prev,
      [key]: target.hasOwnProperty(key) ? target[key] : value
    }),
    {}
  );
};

export const arrayToObject = (data) => {
  return data.reduce((prev, key) => ({ ...prev, [key]: key }), {});
};
